import { useEffect, useState } from 'react';

interface InfiniteScrollOptions {
  /** The element that scrolls; the viewport when null. */
  root: Element | null;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  fetchNextPage: () => unknown;
  /** Stop asking on its own, for example after a page failed to load. */
  paused?: boolean;
  /** How far ahead of the end the next page is asked for, as a CSS margin. */
  rootMargin: string;
}

/**
 * Load the next page before the reader reaches the end of the list.
 *
 * Watches a sentinel placed after the last row. The observer is made afresh
 * each time a page lands, so a screen tall enough to show the sentinel even
 * after the new rows asks again rather than stopping.
 *
 * The root must be the element that scrolls: an observer on the viewport sees
 * the sentinel clipped by its scrolling ancestor and ignores the margin.
 *
 * @returns A ref for the sentinel element.
 */
export const useInfiniteScroll = ({
  root,
  hasNextPage,
  isFetchingNextPage,
  fetchNextPage,
  paused = false,
  rootMargin,
}: InfiniteScrollOptions) => {
  const [ sentinel, setSentinel ] = useState<Element | null>(null);

  useEffect(() => {
    if (!sentinel || !hasNextPage || isFetchingNextPage || paused) {
      return undefined;
    }
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) {
        fetchNextPage();
      }
    }, { root, rootMargin });
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [ sentinel, root, hasNextPage, isFetchingNextPage, paused, fetchNextPage, rootMargin ]);

  return setSentinel;
};
